import { SlideablePanel } from "@/components/ui/SlideablePanel";
import { closeLeftSidebarAtom, leftSidebarOpenAtom, toggleLeftSidebarAtom } from "@/utils/sidebarStore";
import { useAtom, useSetAtom } from "jotai";
import { ReactNode, useEffect, useState } from "react";
import { NavBar } from "./NavBar";
import { SkipToMainLink } from "./SkipToMainLink";

const NAV_HEIGHT = "3.5rem";
const DEFAULT_SIDEBAR_ID = "left-sidebar";
// Wide enough that a session title shows five or so words before truncating.
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 240;
const MAX_WIDTH = 560;
const STORAGE_KEY = "paperclip-sidebar-width-v3";
const COLLAPSED_KEY = "paperclip-sidebar-collapsed";

function getStoredWidth(): number {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const n = parseInt(v, 10);
      if (n >= MIN_WIDTH && n <= MAX_WIDTH) return n;
    }
  } catch { }
  return DEFAULT_WIDTH;
}

type PageLayoutProps = {
  children: ReactNode;
  leftSidebar?: ReactNode;
  sidebarId?: string;
  sidebarLabel?: string;
};

export const PageLayout = ({
  children,
  leftSidebar,
  sidebarId = DEFAULT_SIDEBAR_ID,
  sidebarLabel = "Sidebar navigation",
}: PageLayoutProps) => {
  const [leftSidebarOpen] = useAtom(leftSidebarOpenAtom);
  const closeLeftSidebar = useSetAtom(closeLeftSidebarAtom);
  const toggleLeftSidebar = useSetAtom(toggleLeftSidebarAtom);

  const [width, setWidth] = useState(getStoredWidth);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch { }
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch { }
  }, [width]);

  return (
    <div className="layout flex h-dvh flex-col overflow-hidden bg-white">
      <SkipToMainLink />
      <NavBar
        onLeftSidebarToggle={leftSidebar ? toggleLeftSidebar : undefined}
        leftSidebarOpen={leftSidebarOpen}
        leftSidebarControlsId={sidebarId}
        showPrimaryLinks={!leftSidebar}
      />
      <div className="layout-items flex min-h-0 flex-1 overflow-hidden">
        {leftSidebar && (
          <SlideablePanel
            id={sidebarId}
            isOpen={leftSidebarOpen}
            onClose={closeLeftSidebar}
            from="left"
            offsetTop={NAV_HEIGHT}
            width={`${width}px`}
            alwaysOpenOnDesktop={!collapsed}
            ariaLabel={sidebarLabel}
            panelClassName="lg:border-r-0"
            resizability={{
              onWidthChange: setWidth,
              minWidth: MIN_WIDTH,
              maxWidth: MAX_WIDTH,
              onCollapse: () => {
                closeLeftSidebar();
                setCollapsed(true);
              },
            }}
          >
            {leftSidebar}
          </SlideablePanel>
        )}
        {leftSidebar && collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Show sidebar"
            title="Show sidebar"
            className="hidden w-2 shrink-0 cursor-e-resize border-r border-zinc-200/70 bg-zinc-50 transition-colors hover:bg-zinc-200 lg:block"
          />
        )}

        <main id="main-content" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};
